import { useLandlordAuthStore } from '@afinius/hooks/store/useLandlordAuthStore';
import { reportSsoFailure } from '@afinius/plugins/sentry';
import { refreshLandlordToken } from '@afinius/utilities/silent-sso';
import type {
  LocationQueryValue,
  NavigationGuardReturn,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteRecordRaw,
} from 'vue-router';

const APP_OPEN_FAILURES_KEY = 'app-open-failures';
const APP_OPEN_FAILURE_WINDOW_MS = 10 * 60_000;
const APP_OPEN_FAILURES_BEFORE_SIGN_IN_AGAIN = 2;

const isFailedSsoRedirect = (to: RouteLocationNormalized) =>
  'returnTo' in to.query || 'reason' in to.query;

const queryValue = (value: LocationQueryValue | LocationQueryValue[] | undefined) =>
  (Array.isArray(value) ? value[0] : value) ?? undefined;

const failedOrigin = (to: RouteLocationNormalized) => {
  const origin = queryValue(to.query.origin);
  if (origin) return origin;

  try {
    const redirectTo = new URL(queryValue(to.query.returnTo) ?? '').searchParams.get('redirectTo');

    return redirectTo ? new URL(redirectTo).origin : undefined;
  } catch {
    return undefined;
  }
};

const recordAppOpenFailure = (): number => {
  try {
    const now = Date.now();
    const stored = JSON.parse(sessionStorage.getItem(APP_OPEN_FAILURES_KEY) ?? '[]') as number[];
    const recentFailures = [
      ...stored.filter((failedAt) => now - failedAt < APP_OPEN_FAILURE_WINDOW_MS),
      now,
    ];

    sessionStorage.setItem(APP_OPEN_FAILURES_KEY, JSON.stringify(recentFailures));

    return recentFailures.length;
  } catch {
    return 1;
  }
};

const clearAppOpenFailures = (): boolean => {
  try {
    sessionStorage.removeItem(APP_OPEN_FAILURES_KEY);

    return true;
  } catch {
    return false;
  }
};

const restrictAuthenticated = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalizedLoaded,
): Promise<NavigationGuardReturn> => {
  const authStore = useLandlordAuthStore();

  if (!authStore.isAuthenticated) {
    return true;
  }

  if (!isFailedSsoRedirect(to)) {
    return { name: 'tenant.index' };
  }

  const refreshResult = await refreshLandlordToken();

  if (refreshResult === 'expired') {
    authStore.clear();
    return true;
  }

  const attempt = recordAppOpenFailure();

  reportSsoFailure({
    reason: queryValue(to.query.reason),
    failure: queryValue(to.query.failure),
    app: queryValue(to.query.app),
    origin: failedOrigin(to),
    attempt,
  });

  if (refreshResult === 'recovered' && attempt >= APP_OPEN_FAILURES_BEFORE_SIGN_IN_AGAIN) {
    clearAppOpenFailures();
    authStore.clear();

    const { failure: _failure, origin: _origin, reason: _reason, ...query } = to.query;

    return { name: 'auth.login', query: { ...query, reason: 'sign-in-again' } };
  }

  return { name: 'tenant.index', query: { reason: 'app-open-failed' } };
};

const restrictUnauthenticated = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalizedLoaded,
): NavigationGuardReturn => {
  const authStore = useLandlordAuthStore();

  if (!authStore.isAuthenticated) {
    return { name: 'auth.login' };
  }

  return true;
};

export const routes: RouteRecordRaw[] = [
  {
    path: 'login',
    name: 'auth.login',
    component: () => import('./views/pages/AuthLoginPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Inloggen',
    },
  },
  {
    path: 'switch-to-sso',
    name: 'auth.switch-to-sso',
    component: () => import('./views/pages/AuthSwitchToSsoPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Overschakelen naar Microsoft',
    },
  },
  {
    path: 'register',
    name: 'auth.register',
    component: () => import('./views/pages/AuthRegisterPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Registreren',
    },
  },
  {
    path: 'email-check',
    name: 'auth.email-check',
    component: () => import('./views/pages/AuthEmailCheckPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Controleer je inbox',
    },
  },
  {
    path: 'email-verify',
    name: 'auth.email-verify',
    component: () => import('./views/pages/AuthEmailVerifyPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'E-mail verificatie',
    },
  },
  {
    path: 'forgot-password',
    name: 'auth.forgot-password',
    component: () => import('./views/pages/AuthForgotPasswordPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Wachtwoord vergeten',
    },
  },
  {
    path: 'reset-password',
    name: 'auth.reset-password',
    component: () => import('./views/pages/AuthResetPasswordPage.vue'),
    beforeEnter: restrictAuthenticated,
    meta: {
      title: 'Wachtwoord aanmaken',
    },
  },
  {
    path: 'logout',
    name: 'auth.logout',
    component: () => import('./views/pages/AuthLogoutPage.vue'),
    beforeEnter: restrictUnauthenticated,
    meta: {
      title: 'Uitloggen',
    },
  },
  {
    path: 'terms',
    name: 'auth.terms',
    component: () => import('./views/pages/AuthTermsPage.vue'),
    beforeEnter: restrictUnauthenticated,
    meta: {
      title: 'Privacybeleid',
      hideTitle: true,
    },
  },
];
